import { useEffect, useLayoutEffect, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  useViewport,
  useReactFlow,
  ReactFlowProvider,
  SelectionMode,
  type Connection,
  type Edge as ReactFlowEdge,
  type Node as ReactFlowNode,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useFlowBuilderStore } from '../stores/flowBuilderStore';
import { useAppStore } from '../stores/appStore';
import { toast } from '../stores/toastStore';
import { api } from '../lib/api';
import { CustomNode } from '../components/flow-builder/CustomNode';
import { CustomEdge } from '../components/flow-builder/CustomEdge';
import { InstructionNote } from '../components/flow-builder/InstructionNote';
import { NodePalette } from '../components/flow-builder/NodePalette';
import { NodeConfigPanel } from '../components/flow-builder/NodeConfigPanel';
import { TestDrawer } from '../components/flow-builder/TestDrawer';
import { FlowContextMenu, type CtxItem } from '../components/flow-builder/FlowContextMenu';
import { Button, Badge } from '../components/ui';
import {
  Save,
  Play,
  ArrowLeft,
  PencilLine,
  Check,
  X,
  Pencil,
  Copy,
  CopyPlus,
  ClipboardPaste,
  Unlink,
  Trash2,
  Network,
  Maximize2,
  PlayCircle,
  Layers,
  Undo2,
  Redo2,
  Download,
} from 'lucide-react';
import type { FlowNode as FlowNodeType, FlowEdge, NodeType } from '../types';
import { isPartnerReadOnlyMode } from '../lib/readOnlyMode';
import { useTranslation } from 'react-i18next';

const nodeTypes = {
  custom: CustomNode,
  instruction: InstructionNote,
};

const edgeTypes = {
  custom: CustomEdge,
};

type CopiedSelection = {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  pasteCount: number;
};

function deepClone<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

function generateFlowElementId(prefix = ''): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}${crypto.randomUUID()}`;
  }

  const random = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

  return `${prefix}${random}`;
}

const INSTRUCTION_NOTE_TOP_Y = 20;
const INSTRUCTION_NOTE_LEFT_GAP = 40;
const INSTRUCTION_NOTE_WIDTH = 340;
const INSTRUCTION_NOTE_NODE_GAP = 40;
const INSTRUCTION_NOTE_OFFSET_FROM_FLOW = INSTRUCTION_NOTE_WIDTH + INSTRUCTION_NOTE_NODE_GAP;
const INSTRUCTION_NOTE_MIN_FLOW_X = INSTRUCTION_NOTE_LEFT_GAP + INSTRUCTION_NOTE_OFFSET_FROM_FLOW;
const DEFAULT_MESSAGE_GROUPING_WINDOW_SECONDS = 5;
const MIN_MESSAGE_GROUPING_WINDOW_SECONDS = 1;
const MAX_MESSAGE_GROUPING_WINDOW_SECONDS = 120;

function normalizeMessageGroupingWindowSeconds(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_MESSAGE_GROUPING_WINDOW_SECONDS;
  }

  return Math.min(
    MAX_MESSAGE_GROUPING_WINDOW_SECONDS,
    Math.max(MIN_MESSAGE_GROUPING_WINDOW_SECONDS, Math.round(parsed)),
  );
}

function getInstructionNotePosition(anchorX: number): { x: number; y: number } {
  return {
    x: Math.max(anchorX - INSTRUCTION_NOTE_OFFSET_FROM_FLOW, INSTRUCTION_NOTE_LEFT_GAP),
    y: INSTRUCTION_NOTE_TOP_Y,
  };
}

function normalizeSearchContactHandlesOnLoad(
  flowNodes: FlowNodeType[],
  flowEdges: FlowEdge[],
): FlowEdge[] {
  const searchModeByNodeId = new Map<string, 'contact_company' | 'deal'>();

  for (const node of flowNodes || []) {
    if (node.type !== 'SEARCH_CONTACT') {
      continue;
    }

    const mode = (node.config as any)?.searchIn === 'deal' ? 'deal' : 'contact_company';
    searchModeByNodeId.set(node.id, mode);
  }

  const remapped = (flowEdges || []).map((edge) => {
    const mode = searchModeByNodeId.get(edge.sourceNodeId);
    if (!mode) {
      return edge;
    }

    if (mode === 'deal' && (edge.sourceHandle === 'contact_found' || edge.sourceHandle === 'company_found')) {
      return { ...edge, sourceHandle: 'deal_found' };
    }

    if (mode === 'contact_company' && edge.sourceHandle === 'deal_found') {
      return { ...edge, sourceHandle: 'contact_found' };
    }

    return edge;
  });

  const seen = new Set<string>();
  const deduplicated: FlowEdge[] = [];
  for (const edge of remapped) {
    const dedupeKey = [
      edge.sourceNodeId,
      edge.sourceHandle || '',
      edge.targetNodeId,
    ].join('|');

    if (seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    deduplicated.push(edge);
  }

  return deduplicated;
}

/** React Flow nodes → persisted FlowNode[] (API shape). Shared by Save and Export. */
function toPersistedNodes(nodes: ReactFlowNode[], flowId: string): FlowNodeType[] {
  return nodes
    .filter((node) => node.id !== '__instruction_note__')
    .map((node) => {
      const uiType = node.data.type as NodeType;
      const isCrmCreateAlias =
        uiType === 'CREATE_COMPANY' || uiType === 'CREATE_CONTACT' || uiType === 'CREATE_DEAL';
      const isMoveDealAlias = uiType === 'MOVE_DEAL_STAGE';
      const isGetCrmAlias = uiType === 'GET_CRM_ENTITY';
      const isParseJsonAlias = uiType === 'PARSE_JSON';
      const persistedType: NodeType = isCrmCreateAlias
        ? 'CREATE_LEAD'
        : isMoveDealAlias || isGetCrmAlias
          ? 'UPDATE_CONTACT'
          : isParseJsonAlias
            ? 'SET_VARIABLE'
          : uiType;
      const baseConfig = (node.data.config || {}) as Record<string, unknown>;
      const persistedConfig = isCrmCreateAlias
        ? {
            ...baseConfig,
            mode:
              uiType === 'CREATE_CONTACT'
                ? 'create_contact'
                : uiType === 'CREATE_DEAL'
                  ? 'create_deal'
                  : 'create_company',
          }
        : isMoveDealAlias
          ? {
              ...baseConfig,
              mode: 'move_deal_stage',
            }
          : isGetCrmAlias
            ? {
                ...baseConfig,
                mode: 'get_crm_entity',
              }
          : isParseJsonAlias
            ? {
                ...baseConfig,
                mode: 'parse_json',
              }
        : baseConfig;

      return {
        id: node.id,
        flowId: flowId,
        type: persistedType,
        name: node.data.label as string,
        config: persistedConfig,
        position: node.position,
      };
    });
}

/** React Flow edges → persisted FlowEdge[] (API shape). Shared by Save and Export. */
function toPersistedEdges(edges: ReactFlowEdge[]): FlowEdge[] {
  return edges.map((edge) => ({
    id: edge.id,
    sourceNodeId: edge.source,
    targetNodeId: edge.target,
    sourceHandle: edge.sourceHandle || undefined,
  }));
}

/** Mirrors the viewport translation into CSS vars so the pane's dotted grid pans with the flow. */
function FlowbuildBackground({ wrapperRef }: { wrapperRef: React.RefObject<HTMLDivElement | null> }) {
  const { x, y } = useViewport();
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.style.setProperty('--fb-bg-x', `${x}px`);
    el.style.setProperty('--fb-bg-y', `${y}px`);
  }, [x, y, wrapperRef]);
  return null;
}

function FlowBuilderContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tenant } = useAppStore();
  const {
    flowId,
    flowName,
    nodes,
    edges,
    selectedNode,
    isPanelOpen,
    panelMode,
    isDirty,
    setFlowId,
    setConnectionId,
    setFlowName,
    setNodes,
    setEdges,
    setBitrixFields,
    onNodesChange,
    onEdgesChange,
    onConnect,
    selectNode,
    duplicateNode,
    setDirty,
    reset,
  } = useFlowBuilderStore();

  const { screenToFlowPosition, fitView } = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const copiedSelectionRef = useRef<CopiedSelection | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [saveText, setSaveText] = useState(t('flowBuilder.defaults.save'));
  const [publishText, setPublishText] = useState(t('flowBuilder.defaults.publish'));
  const [isEditingFlowName, setIsEditingFlowName] = useState(false);
  const [flowNameDraft, setFlowNameDraft] = useState('');
  const [isRenamingFlow, setIsRenamingFlow] = useState(false);
  const [flowExecutionMode, setFlowExecutionMode] = useState<'conventional' | 'continuous'>('conventional');
  const [messageGroupingEnabled, setMessageGroupingEnabled] = useState(false);
  const [messageGroupingWindowSeconds, setMessageGroupingWindowSeconds] = useState(
    DEFAULT_MESSAGE_GROUPING_WINDOW_SECONDS,
  );
  const [showExecutionModeModal, setShowExecutionModeModal] = useState(false);
  const [isExecutionModeRequired, setIsExecutionModeRequired] = useState(false);
  const isReadOnlyMode = isPartnerReadOnlyMode();
  const deleteNode = useFlowBuilderStore((state) => state.deleteNode);
  const undo = useFlowBuilderStore((state) => state.undo);
  const redo = useFlowBuilderStore((state) => state.redo);
  const canUndo = useFlowBuilderStore((state) => state.past.length > 0);
  const canRedo = useFlowBuilderStore((state) => state.future.length > 0);
  const [isTestOpen, setIsTestOpen] = useState(false);
  const [testStartNodeId, setTestStartNodeId] = useState<string | null>(null);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; nodeId: string | null } | null>(null);
  const [flowStatus, setFlowStatus] = useState<'draft' | 'published' | 'archived'>('draft');
  const flowDescriptionRef = useRef<string>('');

  // Load flow data
  useEffect(() => {
    if (id === 'new') {
      // Create a new flow immediately
      const createNewFlow = async () => {
        try {
          if (!tenant?.id) {
             console.error('No tenant ID found');
             return;
          }
          const tenantId = tenant.id;
          
          // Fetch connections to get a default connectionId
          const connections = await api.getConnections(tenantId);
          const connectionId = connections.length > 0 ? connections[0].id : undefined;

          const newFlow = await api.createFlow(tenantId, {
            name: t('flowBuilder.defaults.defaultFlowName'),
            description: t('flowBuilder.defaults.defaultFlowDescription'),
            connectionId
          });

          // Redirect to the new flow ID
          navigate(`/flows/${newFlow.id}`, { replace: true });
        } catch (error) {
          console.error('Failed to create new flow:', error);
          setFlowId(null);
          setFlowName(t('flowBuilder.defaults.newFlow'));
          setNodes([]);
          setEdges([]);
        }
      };
      
      createNewFlow();
    } else if (id && id !== flowId) {
      loadFlow(id);
    }

    return () => {
      reset();
    };
  }, [id]);

  const loadFlow = async (loadFlowId: string) => {
    try {
      const flow = await api.getFlow(loadFlowId);
      setFlowId(flow.id);
      setConnectionId(flow.connectionId);
      setFlowName(flow.name);
      flowDescriptionRef.current = flow.description ?? '';
      setFlowStatus((((flow as any).status as string) || 'draft') as 'draft' | 'published' | 'archived');

      const hasExecutionMode =
        flow.settings?.executionMode === 'continuous' ||
        flow.settings?.executionMode === 'conventional';

      setFlowExecutionMode(flow.settings?.executionMode === 'continuous' ? 'continuous' : 'conventional');
      setMessageGroupingEnabled(flow.settings?.messageGroupingEnabled === true);
      setMessageGroupingWindowSeconds(
        normalizeMessageGroupingWindowSeconds(flow.settings?.messageGroupingWindowSeconds),
      );
      setIsExecutionModeRequired(!hasExecutionMode);
      setShowExecutionModeModal(!hasExecutionMode);

      // Fetch Bitrix fields
      if (flow.connectionId) {
        api.getBitrixFields(flow.connectionId)
          .then(fields => setBitrixFields(fields))
          .catch(err => {
            console.error('Failed to load Bitrix fields', err);
            setBitrixFields({}); // Stop loading
          });
      } else {
        setBitrixFields({}); // No connection, stop loading
      }

      // Convert flow nodes to React Flow nodes
      const rfNodes = (flow.nodes || []).map((node: FlowNodeType) => {
        const persistedMode = String((node.config as any)?.mode || '').trim().toLowerCase();
        const uiNodeType =
          node.type === 'CREATE_LEAD' && persistedMode === 'create_company'
            ? 'CREATE_COMPANY'
            : node.type === 'CREATE_LEAD' && persistedMode === 'create_contact'
              ? 'CREATE_CONTACT'
            : node.type === 'CREATE_LEAD' && persistedMode === 'create_deal'
                ? 'CREATE_DEAL'
                : node.type === 'UPDATE_CONTACT' && persistedMode === 'get_crm_entity'
                  ? 'GET_CRM_ENTITY'
                : node.type === 'UPDATE_CONTACT' && persistedMode === 'move_deal_stage'
                  ? 'MOVE_DEAL_STAGE'
                  : node.type === 'SET_VARIABLE' && persistedMode === 'parse_json'
                    ? 'PARSE_JSON'
                : node.type;

        return {
          id: node.id,
          type: 'custom',
          position: node.position,
          data: {
            type: uiNodeType,
            label: node.name || getNodeLabel(uiNodeType, t),
            color: getNodeColor(uiNodeType),
            config: node.config,
          },
        };
      });

      // Reserve space on the left for the quick guide so it never overlaps initial nodes.
      const minFlowNodeX =
        rfNodes.length > 0
          ? Math.min(...rfNodes.map((n: any) => Number(n.position?.x ?? INSTRUCTION_NOTE_MIN_FLOW_X)))
          : INSTRUCTION_NOTE_MIN_FLOW_X;
      const xShiftForGuide =
        minFlowNodeX < INSTRUCTION_NOTE_MIN_FLOW_X ? INSTRUCTION_NOTE_MIN_FLOW_X - minFlowNodeX : 0;
      const shiftedRfNodes =
        xShiftForGuide > 0
          ? rfNodes.map((node: any) => ({
              ...node,
              position: {
                x: Number(node.position?.x ?? 0) + xShiftForGuide,
                y: Number(node.position?.y ?? 0),
              },
            }))
          : rfNodes;

      // Add the instruction note (informational node, always present)
      const startNode = shiftedRfNodes.find((n: any) => n.data.type === 'START');
      const fallbackAnchorX =
        shiftedRfNodes.length > 0
          ? Math.min(...shiftedRfNodes.map((n: any) => Number(n.position?.x ?? INSTRUCTION_NOTE_MIN_FLOW_X)))
          : INSTRUCTION_NOTE_MIN_FLOW_X;
      const instructionNotePosition = getInstructionNotePosition(
        startNode?.position?.x ?? fallbackAnchorX,
      );
      shiftedRfNodes.push({
        id: '__instruction_note__',
        type: 'instruction',
        position: instructionNotePosition,
        data: {
          collapseVersion: `${flow.id}:${Date.now()}`,
        },
        selectable: false,
        connectable: false,
      } as any);

      // Normalize legacy handle IDs for SEARCH_CONTACT before rendering.
      const normalizedEdges = normalizeSearchContactHandlesOnLoad(flow.nodes || [], flow.edges || []);

      // Convert flow edges to React Flow edges
      const rfEdges = normalizedEdges.map((edge: FlowEdge) => ({
        id: edge.id,
        source: edge.sourceNodeId,
        target: edge.targetNodeId,
        sourceHandle: edge.sourceHandle || undefined,
        type: 'custom',
        animated: false,
      }));

      setNodes(shiftedRfNodes);
      setEdges(rfEdges);
      setDirty(false);
      useFlowBuilderStore.getState().clearHistory();
    } catch (error) {
      console.error('Error loading flow:', error);
    }
  };

  const handleSave = async (): Promise<boolean> => {
    if (isReadOnlyMode) {
      return false;
    }

    if (!flowId) {
      console.error('Cannot save: flowId is not set');
      setSaveText(t('flowBuilder.states.error'));
      setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
      return false;
    }

    if (isExecutionModeRequired) {
      setShowExecutionModeModal(true);
      setSaveText(t('flowBuilder.states.chooseMode'));
      setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
      return false;
    }

    // Prevent multiple simultaneous saves
    if (isSaving) {
      console.log('Save already in progress, skipping');
      return false;
    }

    setIsSaving(true);

    try {
      const flowNodes: FlowNodeType[] = toPersistedNodes(nodes, flowId);

      const invalidJsonAutoNode = flowNodes.find((node) => {
        if (node.type !== 'UPDATE_CONTACT') {
          return false;
        }

        const config = (node.config || {}) as Record<string, any>;
        const mode = String(config.mode || '').trim().toLowerCase();
        const mappingMode = String(config.mappingMode || '').trim().toLowerCase();
        if (mode !== 'update_crm' || mappingMode !== 'json_auto') {
          return false;
        }

        if (config.jsonSourceVariable === undefined || config.jsonSourceVariable === null) {
          return true;
        }

        return String(config.jsonSourceVariable).trim().length === 0;
      });

      if (invalidJsonAutoNode) {
        toast.error(
          'No nó "Editar CRM" em modo JSON automático, preencha "Variável com JSON de entrada".',
          'Validação do fluxo',
        );
        setSaveText(t('flowBuilder.states.error'));
        setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
        return false;
      }

      const invalidCreateDealJsonAutoNode = flowNodes.find((node) => {
        if (node.type !== 'CREATE_LEAD') {
          return false;
        }

        const config = (node.config || {}) as Record<string, any>;
        const mode = String(config.mode || '').trim().toLowerCase();
        const mappingMode = String(config.mappingMode || '').trim().toLowerCase();
        if (mode !== 'create_deal' || mappingMode !== 'json_auto') {
          return false;
        }

        if (config.jsonSourceVariable === undefined || config.jsonSourceVariable === null) {
          return true;
        }

        return String(config.jsonSourceVariable).trim().length === 0;
      });

      if (invalidCreateDealJsonAutoNode) {
        toast.error(
          'No nó "Criar Negócio" em modo JSON automático, preencha "Variável com JSON de entrada".',
          'Validação do fluxo',
        );
        setSaveText(t('flowBuilder.states.error'));
        setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
        return false;
      }

      const invalidUpdateCrmEntityIdNode = flowNodes.find((node) => {
        if (node.type !== 'UPDATE_CONTACT') {
          return false;
        }

        const config = (node.config || {}) as Record<string, any>;
        const mode = String(config.mode || '').trim().toLowerCase();
        if (mode !== 'update_crm') {
          return false;
        }

        if (config.entityId === undefined || config.entityId === null) {
          return true;
        }

        return String(config.entityId).trim().length === 0;
      });

      if (invalidUpdateCrmEntityIdNode) {
        toast.error(
          'No nó "Editar CRM", preencha "ID da entidade para editar".',
          'Validação do fluxo',
        );
        setSaveText(t('flowBuilder.states.error'));
        setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
        return false;
      }

      const invalidMoveDealIdNode = flowNodes.find((node) => {
        if (node.type !== 'UPDATE_CONTACT') {
          return false;
        }

        const config = (node.config || {}) as Record<string, any>;
        const mode = String(config.mode || '').trim().toLowerCase();
        if (mode !== 'move_deal_stage') {
          return false;
        }

        // Lead: exige o status de destino (o ID do lead cai em {{lead_id}} por padrão).
        if (String(config.targetEntity || '').trim().toLowerCase() === 'lead') {
          return String(config.statusId ?? '').trim().length === 0;
        }

        // Negócio: exige a variável do ID do negócio.
        if (config.dealId === undefined || config.dealId === null) {
          return true;
        }

        return String(config.dealId).trim().length === 0;
      });

      if (invalidMoveDealIdNode) {
        const isLeadMove = String((invalidMoveDealIdNode.config as any)?.targetEntity || '').trim().toLowerCase() === 'lead';
        toast.error(
          isLeadMove
            ? 'No nó "Mover Etapa/Pipeline" (Lead), selecione o "Status de destino".'
            : 'No nó "Mover Etapa/Pipeline", preencha "Variável do ID do negócio".',
          'Validação do fluxo',
        );
        setSaveText(t('flowBuilder.states.error'));
        setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
        return false;
      }

      const invalidGetCrmEntityIdNode = flowNodes.find((node) => {
        if (node.type !== 'UPDATE_CONTACT') {
          return false;
        }

        const config = (node.config || {}) as Record<string, any>;
        const mode = String(config.mode || '').trim().toLowerCase();
        if (mode !== 'get_crm_entity') {
          return false;
        }

        if (config.entityId === undefined || config.entityId === null) {
          return true;
        }

        return String(config.entityId).trim().length === 0;
      });

      if (invalidGetCrmEntityIdNode) {
        toast.error(
          'No nó "Obter CRM", preencha "ID da entidade para buscar".',
          'Validação do fluxo',
        );
        setSaveText(t('flowBuilder.states.error'));
        setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
        return false;
      }

      const invalidAiResponseVariableNode = flowNodes.find((node) => {
        if (node.type !== 'AI_RESPONSE') {
          return false;
        }

        const config = (node.config || {}) as Record<string, any>;
        if (config.responseVariable === undefined || config.responseVariable === null) {
          return true;
        }

        return String(config.responseVariable).trim().length === 0;
      });

      if (invalidAiResponseVariableNode) {
        toast.error(
          'No nó "IA", preencha "Salvar resposta em".',
          'Validação do fluxo',
        );
        setSaveText(t('flowBuilder.states.error'));
        setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
        return false;
      }

      // Convert React Flow edges back to flow edges
      const flowEdges: FlowEdge[] = toPersistedEdges(edges);

      console.log('Saving flow:', flowId, { nodes: flowNodes.length, edges: flowEdges.length });

      await api.updateFlow(flowId, {
        nodes: flowNodes,
        edges: flowEdges,
        startNodeId: nodes.find((n) => n.data.type === 'START')?.id,
        settings: {
          executionMode: flowExecutionMode,
          messageGroupingEnabled,
          messageGroupingWindowSeconds: normalizeMessageGroupingWindowSeconds(messageGroupingWindowSeconds),
        },
      });

      setDirty(false);
      setSaveText(t('flowBuilder.states.saved'));
      setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
      return true;
    } catch (error) {
      console.error('Error saving flow:', error);
      setSaveText(t('flowBuilder.states.error'));
      setTimeout(() => setSaveText(t('flowBuilder.defaults.save')), 2000);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (isReadOnlyMode) {
      return;
    }

    if (!flowId) {
      console.error('Cannot publish: flowId is not set');
      setPublishText(t('flowBuilder.states.error'));
      setTimeout(() => setPublishText(t('flowBuilder.defaults.publish')), 2000);
      return;
    }
    
    setIsPublishing(true);

    try {
      const saved = await handleSave();
      if (!saved) {
        console.error('Cannot publish: save failed');
        setPublishText(t('flowBuilder.states.saveError'));
        setTimeout(() => setPublishText(t('flowBuilder.defaults.publish')), 2000);
        return;
      }

      console.log('Publishing flow:', flowId);
      await api.publishFlow(flowId);
      
      // Refresh flows in the global store so the list updates
      if (tenant?.id) {
        useAppStore.getState().fetchFlows(tenant.id);
      }
      
      setPublishText(t('flowBuilder.states.published'));
      setFlowStatus('published');
      setTimeout(() => setPublishText(t('flowBuilder.defaults.publish')), 2000);
    } catch (error: any) {
      console.error('Error publishing flow:', error);
      const rawErrors: string[] = Array.isArray(error?.errors) ? error.errors : [];
      const isValidation =
        error?.code === 'VALIDATION_ERROR' ||
        error?.message === 'VALIDATION_ERROR' ||
        rawErrors.length > 0;
      if (isValidation) {
        const friendly = (rawErrors.length
          ? rawErrors
          : ['O fluxo tem um problema que impede a publicação.'])
          .map((e) => {
            const m = /Node "(.+)" has no incoming connections/.exec(e);
            if (m) return `• O nó "${m[1]}" está sem conexão de entrada (nenhuma seta chegando nele).`;
            if (/must have a start node/i.test(e)) return '• O fluxo precisa de um nó de Início.';
            if (/start node not found/i.test(e)) return '• O nó de Início não foi encontrado.';
            return `• ${e}`;
          })
          .join('\n');
        toast.error(friendly, 'Não foi possível publicar o fluxo', 8000);
      } else {
        toast.error(
          String(error?.message || 'Erro ao publicar o fluxo.'),
          'Não foi possível publicar o fluxo',
          8000,
        );
      }
      setPublishText(t('flowBuilder.states.error'));
      setTimeout(() => setPublishText(t('flowBuilder.defaults.publish')), 2000);
    } finally {
      setIsPublishing(false);
    }
  };

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      if (isReadOnlyMode) {
        return;
      }

      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !reactFlowWrapper.current) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      useFlowBuilderStore.getState().addNode(type as NodeType, position);
    },
    [screenToFlowPosition, isReadOnlyMode]
  );

  const isValidConnection = useCallback(
    (connection: Connection | any) => {
      if (isReadOnlyMode) {
        return false;
      }

      // Prevent self-connections
      return connection.source !== connection.target;
    },
    [isReadOnlyMode]
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: unknown) => {
    const n = node as any;
    // Don't open config panel for instruction note
    if (n.id === '__instruction_note__') return;
    selectNode(n);
  }, [selectNode]);

  const onPaneClick = useCallback(() => {
    selectNode(null);
  }, [selectNode]);

  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: unknown) => {
    event.preventDefault();
    const n = node as any;
    if (n.id === '__instruction_note__') return;
    setCtxMenu({ x: event.clientX, y: event.clientY, nodeId: n.id });
  }, []);

  const onPaneContextMenu = useCallback((event: React.MouseEvent | MouseEvent) => {
    event.preventDefault();
    setCtxMenu({ x: event.clientX, y: event.clientY, nodeId: null });
  }, []);

  const selectExecutionMode = (mode: 'conventional' | 'continuous') => {
    if (isReadOnlyMode) {
      return;
    }

    setFlowExecutionMode(mode);
    setDirty(true);
    setIsExecutionModeRequired(false);
  };

  useEffect(() => {
    if (!isEditingFlowName) {
      setFlowNameDraft(flowName || '');
    }
  }, [flowName, isEditingFlowName]);

  const startFlowRename = () => {
    if (isReadOnlyMode) return;
    setFlowNameDraft(flowName || '');
    setIsEditingFlowName(true);
  };

  const cancelFlowRename = () => {
    if (isRenamingFlow) return;
    setFlowNameDraft(flowName || '');
    setIsEditingFlowName(false);
  };

  const saveFlowRename = async () => {
    if (isReadOnlyMode) return;
    if (!flowId || isRenamingFlow) return;

    const nextName = flowNameDraft.trim();
    if (!nextName) {
      toast.error('Informe um nome para o fluxo.');
      return;
    }

    if (nextName === flowName) {
      setIsEditingFlowName(false);
      return;
    }

    setIsRenamingFlow(true);
    try {
      await api.updateFlow(flowId, { name: nextName });
      setFlowName(nextName);
      if (tenant?.id) {
        await useAppStore.getState().fetchFlows(tenant.id);
      }
      toast.success('Nome do fluxo atualizado com sucesso!');
      setIsEditingFlowName(false);
    } catch (error: any) {
      console.error('Error renaming flow:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Erro ao renomear fluxo.');
    } finally {
      setIsRenamingFlow(false);
    }
  };

  const handleNodesChangeSafe = useCallback(
    (changes: any) => {
      if (isReadOnlyMode) return;
      // START and the quick guide can never be removed (keyboard delete, multi-select, etc.)
      const safe = (changes as any[]).filter((change) => {
        if (change?.type !== 'remove') return true;
        if (change.id === '__instruction_note__') return false;
        const node = nodes.find((n) => n.id === change.id);
        return (node?.data as any)?.type !== 'START';
      });
      if (safe.length > 0) onNodesChange(safe);
    },
    [isReadOnlyMode, onNodesChange, nodes],
  );

  const handleEdgesChangeSafe = useCallback(
    (changes: any) => {
      if (isReadOnlyMode) return;
      onEdgesChange(changes);
    },
    [isReadOnlyMode, onEdgesChange],
  );

  const handleConnectSafe = useCallback(
    (connection: any) => {
      if (isReadOnlyMode) return;
      onConnect(connection);
    },
    [isReadOnlyMode, onConnect],
  );

  const handleReorganizeFlow = useCallback(() => {
    if (isReadOnlyMode) {
      return;
    }

    const flowNodes = nodes.filter((node) => node.id !== '__instruction_note__');
    if (flowNodes.length === 0) {
      return;
    }

    const nodeIds = new Set(flowNodes.map((node) => node.id));
    const outgoing = new Map<string, string[]>();
    const incomingCount = new Map<string, number>();
    const incomingByNode = new Map<string, string[]>();

    for (const node of flowNodes) {
      outgoing.set(node.id, []);
      incomingCount.set(node.id, 0);
      incomingByNode.set(node.id, []);
    }

    for (const edge of edges) {
      if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) {
        continue;
      }

      outgoing.get(edge.source)?.push(edge.target);
      incomingCount.set(edge.target, (incomingCount.get(edge.target) || 0) + 1);
      incomingByNode.get(edge.target)?.push(edge.source);
    }

    const startNodeIds = flowNodes
      .filter((node) => (node.data as any)?.type === 'START')
      .map((node) => node.id);

    const roots = startNodeIds.length > 0
      ? startNodeIds
      : flowNodes
          .filter((node) => (incomingCount.get(node.id) || 0) === 0)
          .map((node) => node.id);

    const nodeById = new Map(flowNodes.map((node) => [node.id, node]));

    const minX = Math.min(...flowNodes.map((node) => node.position.x));
    const minY = Math.min(...flowNodes.map((node) => node.position.y));
    const baseX = Number.isFinite(minX) ? minX : INSTRUCTION_NOTE_MIN_FLOW_X + 120;
    const layoutStartX = Math.max(baseX, INSTRUCTION_NOTE_MIN_FLOW_X + 40);
    const baseY = Number.isFinite(minY) ? minY : 80;

    // Ordered output handle ids per node — mirrors CustomNode.getOutputs (LR rows).
    const outputIds = (node: ReactFlowNode): (string | undefined)[] => {
      const nodeType = String((node.data as any)?.type || '');
      const cfg = ((node.data as any)?.config || {}) as Record<string, any>;
      switch (nodeType) {
        case 'CONDITION':
          return [
            ...(Array.isArray(cfg.conditions) ? cfg.conditions.map((c: any) => String(c?.id ?? '')) : []),
            'default',
          ];
        case 'WAIT_CHOICE':
          return (Array.isArray(cfg.validChoices) ? cfg.validChoices : []).map((_: unknown, i: number) => `choice-${i}`);
        case 'SEND_BUTTONS': {
          const ids = (Array.isArray(cfg.buttons) ? cfg.buttons : []).map((_: unknown, i: number) => `button-${i}`);
          if (cfg.fallbackEnabled) ids.push('fallback');
          return ids;
        }
        case 'MESSAGE_TYPE':
          return ['text', 'image', 'audio', 'default'];
        case 'SEARCH_CONTACT':
          if (cfg.searchIn === 'deal') return ['deal_found', 'not_found'];
          if (cfg.searchIn === 'lead') return ['lead_found', 'not_found'];
          return ['contact_found', 'company_found', 'not_found'];
        case 'EXTRACT_DATA':
          return ['found', 'not_found'];
        case 'BUSINESS_HOURS':
          return ['open', 'closed'];
        case 'SWITCH':
          return ['default', 'else'];
        case 'END_SESSION':
          return [];
        default:
          return [undefined];
      }
    };
    const nodeHeightLR = (node: ReactFlowNode): number => {
      const o = outputIds(node).length;
      return o > 1 ? 46 + o * 32 : 96;
    };

    // Parent links carrying the source handle → lets children align with the exact source port.
    const parentLinks = new Map<string, { parent: string; handle: string | null | undefined }[]>();
    for (const edge of edges) {
      if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) continue;
      if (!parentLinks.has(edge.target)) parentLinks.set(edge.target, []);
      parentLinks.get(edge.target)!.push({ parent: edge.source, handle: edge.sourceHandle });
    }

    // Depth = LONGEST path from a root (mockup autoArrange): every node lands to the right of all
    // its parents. Back-edges (cycles) are detected by DFS and ignored for layering.
    const color = new Map<string, number>(); // 0 = unvisited, 1 = on stack, 2 = done
    const backEdges = new Set<string>();
    const dfs = (u: string) => {
      color.set(u, 1);
      for (const v of outgoing.get(u) || []) {
        const c = color.get(v) || 0;
        if (c === 1) backEdges.add(`${u}->${v}`);
        else if (c === 0) dfs(v);
      }
      color.set(u, 2);
    };
    for (const r of roots) if ((color.get(r) || 0) === 0) dfs(r);
    for (const node of flowNodes) if ((color.get(node.id) || 0) === 0) dfs(node.id);

    const level = new Map<string, number>();
    for (const node of flowNodes) level.set(node.id, 0);
    const forwardEdges: [string, string][] = [];
    for (const [u, targets] of outgoing) {
      for (const v of targets) if (!backEdges.has(`${u}->${v}`)) forwardEdges.push([u, v]);
    }
    for (let pass = 0; pass < flowNodes.length; pass++) {
      let changed = false;
      for (const [u, v] of forwardEdges) {
        const d = (level.get(u) || 0) + 1;
        if (d > (level.get(v) || 0)) {
          level.set(v, d);
          changed = true;
        }
      }
      if (!changed) break;
    }

    const byLevel = new Map<number, string[]>();
    for (const node of flowNodes) {
      const lv = level.get(node.id) || 0;
      if (!byLevel.has(lv)) byLevel.set(lv, []);
      byLevel.get(lv)!.push(node.id);
    }
    const maxLevel = Math.max(0, ...byLevel.keys());
    const yOrder = (nid: string) => nodeById.get(nid)?.position?.y ?? 0;

    // Order each layer by parent index + source-port row (fan-outs stay in row order → fewer crossings);
    // ties keep the current visual order, like the mockup.
    const orderByLevel = new Map<number, string[]>();
    for (let lv = 0; lv <= maxLevel; lv++) {
      const ids = [...(byLevel.get(lv) || [])];
      if (lv === 0) {
        ids.sort((a, b) => yOrder(a) - yOrder(b));
      } else {
        const prev = orderByLevel.get(lv - 1) || [];
        const prevIndex = new Map(prev.map((nid, i) => [nid, i]));
        const bary = (nid: string) => {
          const links = (parentLinks.get(nid) || []).filter((l) => prevIndex.has(l.parent));
          if (links.length === 0) return Number.MAX_SAFE_INTEGER;
          const total = links.reduce((sum, l) => {
            const parentNode = nodeById.get(l.parent)!;
            const pIds = outputIds(parentNode);
            const rowIdx = Math.max(0, pIds.indexOf(l.handle ?? undefined));
            return sum + (prevIndex.get(l.parent) || 0) + (rowIdx + 1) / (pIds.length + 1);
          }, 0);
          return total / links.length;
        };
        ids.sort((a, b) => {
          const d = bary(a) - bary(b);
          return d !== 0 ? d : yOrder(a) - yOrder(b);
        });
      }
      orderByLevel.set(lv, ids);
    }

    // Place like the mockup: X by column, stack each column, then center every column
    // vertically against the tallest one.
    const colStep = 304; // 244px card + 60px gap (mockup: 232 + 60)
    const rowGap = 40;
    const stacked = new Map<string, number>();
    const colHeight = new Map<number, number>();
    for (let lv = 0; lv <= maxLevel; lv++) {
      const ids = orderByLevel.get(lv) || [];
      let y = 0;
      for (const nid of ids) {
        stacked.set(nid, y);
        y += nodeHeightLR(nodeById.get(nid)!) + rowGap;
      }
      colHeight.set(lv, Math.max(0, y - rowGap));
    }
    const tallest = Math.max(0, ...colHeight.values());

    const arrangedPositions = new Map<string, { x: number; y: number }>();
    for (let lv = 0; lv <= maxLevel; lv++) {
      const shift = (tallest - (colHeight.get(lv) || 0)) / 2;
      for (const nid of orderByLevel.get(lv) || []) {
        arrangedPositions.set(nid, {
          x: layoutStartX + lv * colStep,
          y: baseY + shift + (stacked.get(nid) || 0),
        });
      }
    }

    const rootsSorted = orderByLevel.get(0) || [...roots];

    const arrangedValues = [...arrangedPositions.values()];
    if (arrangedValues.length > 0) {
      const minArrangedX = Math.min(...arrangedValues.map((position) => position.x));
      const xShift = minArrangedX < INSTRUCTION_NOTE_MIN_FLOW_X
        ? INSTRUCTION_NOTE_MIN_FLOW_X - minArrangedX
        : 0;

      if (xShift > 0) {
        for (const [nodeId, position] of arrangedPositions.entries()) {
          arrangedPositions.set(nodeId, {
            x: position.x + xShift,
            y: position.y,
          });
        }
      }
    }

    const startNodeId = rootsSorted[0] || startNodeIds[0];
    const arrangedStartPos = startNodeId ? arrangedPositions.get(startNodeId) : undefined;
    const nextNodes = nodes.map((node) => {
      if (node.id === '__instruction_note__') {
        return {
          ...node,
          position: getInstructionNotePosition(arrangedStartPos?.x ?? layoutStartX),
        };
      }

      const arranged = arrangedPositions.get(node.id);
      if (!arranged) {
        return node;
      }

      return {
        ...node,
        position: arranged,
        dragging: false,
      };
    });

    useFlowBuilderStore.getState().snapshot();
    setNodes(nextNodes);
    setDirty(true);
    window.requestAnimationFrame(() => {
      fitView({ padding: 0.18, duration: 350 });
    });
  }, [isReadOnlyMode, nodes, edges, setNodes, setDirty, fitView]);

  const copySelectedElements = useCallback((): boolean => {
    const selectedNodes = nodes.filter((node) => node.selected && node.id !== '__instruction_note__');
    if (selectedNodes.length === 0) {
      return false;
    }

    const selectedIds = new Set(selectedNodes.map((node) => node.id));
    const relatedEdges = edges.filter(
      (edge) => selectedIds.has(edge.source) && selectedIds.has(edge.target),
    );

    copiedSelectionRef.current = {
      nodes: selectedNodes.map((node) => ({
        ...node,
        data: deepClone(node.data),
        selected: false,
        dragging: false,
      })),
      edges: relatedEdges.map((edge) => ({
        ...edge,
        selected: false,
      })),
      pasteCount: 0,
    };

    return true;
  }, [nodes, edges]);

  const pasteCopiedElements = useCallback((): boolean => {
    const copied = copiedSelectionRef.current;
    if (!copied || copied.nodes.length === 0) {
      return false;
    }

    useFlowBuilderStore.getState().snapshot();
    copied.pasteCount += 1;
    const offset = 40 * copied.pasteCount;
    const nodeIdMap = new Map<string, string>();

    const pastedNodes = copied.nodes.map((node) => {
      const newNodeId = generateFlowElementId();
      nodeIdMap.set(node.id, newNodeId);

      return {
        ...node,
        id: newNodeId,
        position: {
          x: node.position.x + offset,
          y: node.position.y + offset,
        },
        selected: true,
        dragging: false,
        data: deepClone(node.data),
      };
    });

    const pastedEdges: ReactFlowEdge[] = [];
    for (const edge of copied.edges) {
      const source = nodeIdMap.get(edge.source);
      const target = nodeIdMap.get(edge.target);
      if (!source || !target) {
        continue;
      }

      pastedEdges.push({
        ...edge,
        id: generateFlowElementId('e_'),
        source,
        target,
        selected: false,
      });
    }

    const nextNodes = nodes.map((node) =>
      node.id === '__instruction_note__'
        ? node
        : {
            ...node,
            selected: false,
          },
    );

    const nextEdges = edges.map((edge) => ({
      ...edge,
      selected: false,
    }));

    setNodes([...nextNodes, ...pastedNodes]);
    setEdges([...nextEdges, ...pastedEdges]);
    selectNode(pastedNodes.length === 1 ? pastedNodes[0] : null);
    setDirty(true);

    return true;
  }, [nodes, edges, selectNode, setDirty, setEdges, setNodes]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isReadOnlyMode) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const isTypingTarget = !!target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      );

      // Undo / redo (Ctrl+Z, Ctrl+Shift+Z, Ctrl+Y) — native text undo wins while typing in a field
      if (!isTypingTarget && (event.metaKey || event.ctrlKey)) {
        const hotkey = event.key.toLowerCase();
        if (hotkey === 'z') {
          event.preventDefault();
          if (event.shiftKey) redo();
          else undo();
          return;
        }
        if (hotkey === 'y') {
          event.preventDefault();
          redo();
          return;
        }
      }

      if (isTypingTarget || !selectedNode || selectedNode.id === '__instruction_note__') {
        if (!isTypingTarget && (event.metaKey || event.ctrlKey)) {
          const key = event.key.toLowerCase();
          if (key === 'c' && copySelectedElements()) {
            event.preventDefault();
          } else if (key === 'v' && pasteCopiedElements()) {
            event.preventDefault();
          }
        }
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        duplicateNode(selectedNode.id);
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'c' && copySelectedElements()) {
        event.preventDefault();
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'v' && pasteCopiedElements()) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isReadOnlyMode, selectedNode, duplicateNode, copySelectedElements, pasteCopiedElements, undo, redo]);

  const handleExport = useCallback(async () => {
    if (!flowId) {
      toast.error('Salve o fluxo antes de exportar.', 'Exportar fluxo');
      return;
    }

    try {
      // The server builds and signs the file; the import only accepts signed ones.
      const payload = await api.exportFlow(flowId);
      const json = JSON.stringify(payload, null, 2);
      const slug =
        (flowName || 'fluxo')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '') || 'fluxo';
      const filename = `${slug}-${new Date().toISOString().slice(0, 10)}.ibot-flow.json`;

      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1500);

      const nodeCount = Array.isArray(payload?.flow?.nodes) ? payload.flow.nodes.length : 0;
      const edgeCount = Array.isArray(payload?.flow?.edges) ? payload.flow.edges.length : 0;
      toast.success(`${nodeCount} passos e ${edgeCount} ligações → ${filename}`, 'Fluxo exportado', 5000);
    } catch (error: any) {
      console.error('Export failed:', error);
      toast.error(error?.message || 'Não foi possível exportar o fluxo.', 'Exportar fluxo', 8000);
    }
  }, [flowId, flowName]);

  const flowNodeCount = nodes.filter((n) => n.id !== '__instruction_note__').length;
  const selectedNodeIds = nodes.filter((n) => n.selected && n.id !== '__instruction_note__').map((n) => n.id);
  const selectedCount = selectedNodeIds.length;

  const buildContextItems = (): CtxItem[] => {
    if (!ctxMenu) return [];

    // multi-selection menu (mockup): right-click on one of the selected nodes or on the canvas
    const isMulti = selectedCount > 1 && (!ctxMenu.nodeId || selectedNodeIds.includes(ctxMenu.nodeId));
    if (isMulti && !isReadOnlyMode) {
      const sel = new Set(selectedNodeIds);
      const deletable = new Set(
        nodes.filter((n) => sel.has(n.id) && (n.data as any)?.type !== 'START').map((n) => n.id),
      );
      return [
        { key: 'count', label: `${selectedCount} passos selecionados`, icon: Layers, disabled: true, onClick: () => {} },
        {
          key: 'dup',
          label: 'Duplicar seleção',
          icon: CopyPlus,
          sep: true,
          onClick: () => {
            if (copySelectedElements()) pasteCopiedElements();
          },
        },
        {
          key: 'unlink',
          label: 'Remover conexões',
          icon: Unlink,
          onClick: () => {
            useFlowBuilderStore.getState().snapshot();
            setEdges(edges.filter((edge) => !sel.has(edge.source) && !sel.has(edge.target)));
            setDirty(true);
          },
        },
        { key: 'arrange', label: 'Organizar layout', icon: Network, onClick: handleReorganizeFlow },
        {
          key: 'clear',
          label: 'Limpar seleção',
          icon: X,
          sep: true,
          onClick: () => {
            setNodes(nodes.map((n) => (n.selected ? { ...n, selected: false } : n)));
            setEdges(edges.map((edge) => (edge.selected ? { ...edge, selected: false } : edge)));
            selectNode(null);
          },
        },
        {
          key: 'del',
          label: 'Excluir seleção',
          icon: Trash2,
          danger: true,
          hint: 'Del',
          onClick: () => {
            useFlowBuilderStore.getState().snapshot();
            setNodes(nodes.filter((n) => !deletable.has(n.id)));
            setEdges(edges.filter((edge) => !deletable.has(edge.source) && !deletable.has(edge.target)));
            setDirty(true);
            selectNode(null);
          },
        },
      ];
    }
    const hasClip = !!copiedSelectionRef.current;
    const fit = () => void fitView({ padding: 0.18, duration: 300 });
    const openTest = (fromNodeId: string | null) => {
      setTestStartNodeId(fromNodeId);
      setIsTestOpen(true);
    };
    const copyNodeById = (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;
      copiedSelectionRef.current = {
        nodes: [{ ...node, data: deepClone(node.data), selected: false, dragging: false }],
        edges: [],
        pasteCount: 0,
      };
    };

    if (ctxMenu.nodeId) {
      const nodeId = ctxMenu.nodeId;
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return [];
      const isStart = (node.data as any)?.type === 'START';
      if (isReadOnlyMode) {
        return [
          { key: 'test', label: 'Testar daqui', icon: PlayCircle, onClick: () => openTest(nodeId) },
          { key: 'fit', label: 'Enquadrar', icon: Maximize2, onClick: fit },
        ];
      }
      const items: CtxItem[] = [
        { key: 'edit', label: 'Editar passo', icon: Pencil, onClick: () => selectNode(node) },
        { key: 'test', label: 'Testar daqui', icon: PlayCircle, onClick: () => openTest(nodeId) },
      ];
      if (!isStart) {
        items.push(
          { key: 'dup', label: 'Duplicar', icon: CopyPlus, sep: true, onClick: () => duplicateNode(nodeId) },
          { key: 'copy', label: 'Copiar', icon: Copy, hint: 'Ctrl C', onClick: () => copyNodeById(nodeId) },
        );
      }
      items.push({
        key: 'paste',
        label: 'Colar aqui',
        icon: ClipboardPaste,
        hint: 'Ctrl V',
        disabled: !hasClip,
        sep: isStart,
        onClick: () => {
          pasteCopiedElements();
        },
      });
      items.push({
        key: 'unlink',
        label: 'Remover conexões',
        icon: Unlink,
        sep: true,
        onClick: () => {
          useFlowBuilderStore.getState().snapshot();
          setEdges(edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
          setDirty(true);
        },
      });
      if (!isStart) {
        items.push({ key: 'del', label: 'Excluir', icon: Trash2, danger: true, hint: 'Del', onClick: () => deleteNode(nodeId) });
      }
      return items;
    }

    const paneItems: CtxItem[] = [];
    if (!isReadOnlyMode) {
      paneItems.push(
        { key: 'undo', label: 'Desfazer', icon: Undo2, hint: 'Ctrl Z', disabled: !canUndo, onClick: undo },
        { key: 'redo', label: 'Refazer', icon: Redo2, hint: 'Ctrl ⇧ Z', disabled: !canRedo, onClick: redo },
      );
    }
    if (!isReadOnlyMode) {
      paneItems.push({
        key: 'paste',
        label: 'Colar',
        icon: ClipboardPaste,
        hint: 'Ctrl V',
        disabled: !hasClip,
        sep: true,
        onClick: () => {
          pasteCopiedElements();
        },
      });
      paneItems.push({ key: 'arrange', label: 'Organizar layout', icon: Network, sep: true, onClick: handleReorganizeFlow });
    }
    paneItems.push({ key: 'fit', label: 'Enquadrar', icon: Maximize2, onClick: fit });
    paneItems.push({ key: 'test', label: 'Testar fluxo', icon: PlayCircle, sep: true, onClick: () => openTest(null) });
    paneItems.push({ key: 'export', label: 'Exportar fluxo (JSON)', icon: Download, onClick: handleExport });
    return paneItems;
  };

  return (
    <div className="flowbuild dark h-screen flex flex-col bg-slate-950">
      {isReadOnlyMode && (
        <div className="bg-amber-500 text-amber-950 px-4 py-2 text-sm font-medium text-center">
          {t('flowBuilder.readOnlyBanner')}
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-4 py-2">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/flows')}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
          </button>
          <div>
            {!isEditingFlowName ? (
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-slate-900 dark:text-slate-100">
                  {flowName || t('flowBuilder.defaults.newFlow')}
                </h1>
                {!isReadOnlyMode && (
                  <button
                    type="button"
                    onClick={startFlowRename}
                    className="inline-flex items-center justify-center rounded-md p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-slate-100 dark:hover:bg-slate-800"
                    title="Renomear fluxo"
                  >
                    <PencilLine className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={flowNameDraft}
                  onChange={(event) => setFlowNameDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void saveFlowRename();
                    } else if (event.key === 'Escape') {
                      event.preventDefault();
                      cancelFlowRename();
                    }
                  }}
                  autoFocus
                  disabled={isRenamingFlow}
                  className="h-8 w-72 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => void saveFlowRename()}
                  disabled={isRenamingFlow || !flowNameDraft.trim()}
                  className="inline-flex items-center justify-center rounded-md p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed dark:text-emerald-400 dark:hover:bg-emerald-500/10"
                  title="Salvar nome"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={cancelFlowRename}
                  disabled={isRenamingFlow}
                  className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-300 dark:hover:bg-slate-800"
                  title="Cancelar edição"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex items-center gap-2 mt-1">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10.5px] ${
                  flowStatus === 'published'
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                    : 'border-slate-600 bg-slate-800/60 text-slate-300'
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${flowStatus === 'published' ? 'bg-emerald-400' : 'bg-violet-400'}`} />
                {flowStatus === 'published' ? 'Publicado' : flowStatus === 'archived' ? 'Arquivado' : 'Rascunho'}
              </span>
              {isDirty && (
                <Badge variant="warning" size="sm">
                  {t('flowBuilder.defaults.unsaved')}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="mr-2">
            <button
              type="button"
              onClick={() => setShowExecutionModeModal(true)}
              disabled={isReadOnlyMode}
              className={`rounded-lg border px-3 py-1.5 text-xs text-left transition-colors ${
                isExecutionModeRequired
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-500/20 text-amber-800 dark:text-amber-200'
                  : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200'
              }`}
            >
              <span className="font-medium">{t('flowBuilder.executionMode.label')}</span>{' '}
              {flowExecutionMode === 'continuous'
                ? t('flowBuilder.executionMode.freeConversation')
                : t('flowBuilder.executionMode.stepByStep')}
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} title="Exportar fluxo (JSON)">
            <Download className="w-4 h-4 mr-1" />
            Exportar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setTestStartNodeId(null);
              setIsTestOpen(true);
            }}
          >
            <PlayCircle className="w-4 h-4 mr-1" />
            Testar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
            disabled={isReadOnlyMode}
          >
            <Save className="w-4 h-4 mr-1" />
            {saveText}
          </Button>
          <Button
            size="sm"
            onClick={handlePublish}
            isLoading={isPublishing}
            disabled={isReadOnlyMode}
          >
            <Play className="w-4 h-4 mr-1" />
            {publishText}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Flow Canvas */}
        <div className="flex-1 relative bg-slate-950" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChangeSafe}
            onEdgesChange={handleEdgesChangeSafe}
            onConnect={handleConnectSafe}
            isValidConnection={isValidConnection}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onNodeContextMenu={onNodeContextMenu}
            onPaneContextMenu={onPaneContextMenu}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            proOptions={{ hideAttribution: true }}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            selectionOnDrag={!isReadOnlyMode}
            selectionMode={SelectionMode.Partial}
            panOnDrag={[1, 2]}
            panOnScroll
            panActivationKeyCode="Space"
            deleteKeyCode={isReadOnlyMode ? null : ['Backspace', 'Delete']}
            connectionRadius={36}
            minZoom={0.3}
            maxZoom={1.8}
            nodesDraggable={!isReadOnlyMode}
            nodesConnectable={!isReadOnlyMode}
            elementsSelectable={!isReadOnlyMode}
          >
            <FlowbuildBackground wrapperRef={reactFlowWrapper} />
          </ReactFlow>
          <div className="pointer-events-none absolute right-3.5 top-3.5 z-10 flex items-center gap-2 text-[11px] text-slate-500">
            {selectedCount > 1 && (
              <span className="rounded-full border border-violet-500/40 bg-violet-500/15 px-2 py-0.5 text-violet-200">
                {selectedCount} selecionados
              </span>
            )}
            <span>{flowNodeCount} passos</span>
          </div>
        </div>

        {/* Side Panel */}
        {isPanelOpen && panelMode === 'edit' && <NodeConfigPanel />}
      </div>

      {!isReadOnlyMode && (
        <NodePalette
          onReorganize={handleReorganizeFlow}
          canReorganize={nodes.some((node) => node.id !== '__instruction_note__')}
          onTest={() => {
            setTestStartNodeId(null);
            setIsTestOpen(true);
          }}
        />
      )}

      {ctxMenu && (
        <FlowContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={buildContextItems()}
          onClose={() => setCtxMenu(null)}
        />
      )}

      <TestDrawer open={isTestOpen} startNodeId={testStartNodeId} onClose={() => setIsTestOpen(false)} />

      {showExecutionModeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!isExecutionModeRequired) {
                setShowExecutionModeModal(false);
              }
            }}
          />

          <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5">
            <div className="mb-4">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{t('flowBuilder.executionMode.modalTitle')}</h3>
              <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">
                {isExecutionModeRequired
                  ? t('flowBuilder.executionMode.modalRequiredDescription')
                  : t('flowBuilder.executionMode.modalOptionalDescription')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => selectExecutionMode('conventional')}
                className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                  flowExecutionMode === 'conventional'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/20'
                    : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('flowBuilder.executionMode.conventionalTitle')}</p>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                  {t('flowBuilder.executionMode.conventionalDescription')}
                </p>
              </button>

              <button
                type="button"
                onClick={() => selectExecutionMode('continuous')}
                className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                  flowExecutionMode === 'continuous'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/20'
                    : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t('flowBuilder.executionMode.continuousTitle')}</p>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                  {t('flowBuilder.executionMode.continuousDescription')}
                </p>
              </button>
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/40 p-4">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {t('flowBuilder.executionMode.groupingTitle')}
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                {t('flowBuilder.executionMode.groupingDescription')}
              </p>

              <label className="mt-3 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={messageGroupingEnabled}
                  onChange={(event) => setMessageGroupingEnabled(event.target.checked)}
                  disabled={isReadOnlyMode}
                />
                {t('flowBuilder.executionMode.groupingEnableLabel')}
              </label>

              <div className="mt-3 flex items-center gap-2">
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  {t('flowBuilder.executionMode.groupingWindowLabel')}
                </span>
                <input
                  type="number"
                  min={MIN_MESSAGE_GROUPING_WINDOW_SECONDS}
                  max={MAX_MESSAGE_GROUPING_WINDOW_SECONDS}
                  value={messageGroupingWindowSeconds}
                  onChange={(event) =>
                    setMessageGroupingWindowSeconds(
                      normalizeMessageGroupingWindowSeconds(event.target.value),
                    )
                  }
                  disabled={!messageGroupingEnabled || isReadOnlyMode}
                  className="w-24 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-sm text-slate-900 dark:text-slate-100 disabled:opacity-50"
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  {t('flowBuilder.executionMode.groupingSeconds')}
                </span>
              </div>

              <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
                {t('flowBuilder.executionMode.groupingWindowHint')}
              </p>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              {!isExecutionModeRequired && (
                <Button variant="outline" size="sm" onClick={() => setShowExecutionModeModal(false)}>
                  {t('flowBuilder.executionMode.close')}
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => setShowExecutionModeModal(false)}
                disabled={isReadOnlyMode || isExecutionModeRequired}
              >
                {t('flowBuilder.executionMode.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper functions
function getNodeLabel(type: string, t: (key: string) => string): string {
  const labels: Record<string, string> = {
    START: t('flowBuilder.nodeLabels.START'),
    SEND_MESSAGE: t('flowBuilder.nodeLabels.SEND_MESSAGE'),
    SEND_BUTTONS: t('flowBuilder.nodeLabels.SEND_BUTTONS'),
    WAIT_INPUT: t('flowBuilder.nodeLabels.WAIT_INPUT'),
    CONDITION: t('flowBuilder.nodeLabels.CONDITION'),
    MESSAGE_TYPE: t('flowBuilder.nodeLabels.MESSAGE_TYPE'),
    DELAY: t('flowBuilder.nodeLabels.DELAY'),
    AI_RESPONSE: t('flowBuilder.nodeLabels.AI_RESPONSE'),
    HTTP_REQUEST: t('flowBuilder.nodeLabels.HTTP_REQUEST'),
    TRANSFER_TO_OPERATOR: t('flowBuilder.nodeLabels.TRANSFER_TO_OPERATOR'),
    END_SESSION: t('flowBuilder.nodeLabels.END_SESSION'),
    SET_VARIABLE: t('flowBuilder.nodeLabels.SET_VARIABLE'),
    PARSE_JSON: t('flowBuilder.nodeLabels.PARSE_JSON'),
    ADD_TAG: t('flowBuilder.nodeLabels.ADD_TAG'),
    CREATE_LEAD: t('flowBuilder.nodeLabels.CREATE_LEAD'),
    CREATE_COMPANY: t('flowBuilder.nodeLabels.CREATE_COMPANY'),
    CREATE_CONTACT: t('flowBuilder.nodeLabels.CREATE_CONTACT'),
    CREATE_DEAL: t('flowBuilder.nodeLabels.CREATE_DEAL'),
    GET_CRM_ENTITY: t('flowBuilder.nodeLabels.GET_CRM_ENTITY'),
    UPDATE_CONTACT: t('flowBuilder.nodeLabels.UPDATE_CONTACT'),
    MOVE_DEAL_STAGE: t('flowBuilder.nodeLabels.MOVE_DEAL_STAGE'),
  };
  return labels[type] || type;
}

function getNodeColor(type: string): string {
  const colors: Record<string, string> = {
    START: '#22c55e',
    SEND_MESSAGE: '#3b82f6',
    SEND_BUTTONS: '#3b82f6',
    WAIT_INPUT: '#f59e0b',
    CONDITION: '#8b5cf6',
    DELAY: '#64748b',
    AI_RESPONSE: '#ec4899',
    HTTP_REQUEST: '#06b6d4',
    TRANSFER_TO_OPERATOR: '#ef4444',
    END_SESSION: '#ef4444',
    SET_VARIABLE: '#8b5cf6',
    PARSE_JSON: '#7c3aed',
    ADD_TAG: '#f97316',
    CREATE_LEAD: '#94a3b8',
    CREATE_COMPANY: '#0ea5e9',
    CREATE_CONTACT: '#0ea5e9',
    CREATE_DEAL: '#0ea5e9',
    GET_CRM_ENTITY: '#0ea5e9',
    UPDATE_CONTACT: '#14b8a6',
    MOVE_DEAL_STAGE: '#0d9488',
  };
  return colors[type] || '#64748b';
}

export function FlowBuilder() {
  return (
    <ReactFlowProvider>
      <FlowBuilderContent />
    </ReactFlowProvider>
  );
}
